import { useTranslation } from "@next-platform/client-common-core";
import { Button, ButtonVariant, DropDownMenu, IconVariants } from "@next-platform/client-common-spicy-ui";
import { ConversationSortOrder } from "@next-platform/packages-ts-types";
import React, { useCallback, useMemo } from "react";

type ChatsSortDropdownProps = {
  value: ConversationSortOrder;
  onChange: (sortOrder: ConversationSortOrder) => void;
  "data-testid"?: string;
};

const ChatsSortDropdown = ({ value, onChange, "data-testid": testId = "ChatsSortDropdown" }: ChatsSortDropdownProps) => {
  const { t } = useTranslation();

  const sortOptions = useMemo(
    () => [
      {
        label: t("chats:page.sort.recent"),
        value: ConversationSortOrder.LATEST,
      },
      {
        label: t("chats:page.sort.oldest"),
        value: ConversationSortOrder.OLDEST,
      },
      {
        label: t("chats:page.sort.mostMessages"),
        value: ConversationSortOrder.MOST_MESSAGES,
      },
    ],
    [t],
  );

  const handleSortChange = useCallback(
    (sortOrder: ConversationSortOrder) => {
      onChange(sortOrder);
    },
    [onChange],
  );

  const dropdownItems = useMemo(
    () =>
      sortOptions.map((sortOption) => ({
        label: sortOption.label,
        value: sortOption.value,
        dataTestId: `ChatsSortDropdown-${sortOption.value}`,
        onPress: () => handleSortChange(sortOption.value),
      })),
    [handleSortChange, sortOptions],
  );

  const selectedLabel = sortOptions.find((sortOption) => sortOption.value === value)?.label ?? sortOptions[0].label;

  return (
    <div data-testid={testId}>
      <DropDownMenu listItems={dropdownItems}>
        <Button
          aria-label={t("chats:page.sort.ariaLabel")}
          data-testid="ChatsSortDropdown-Trigger"
          iconEnd={IconVariants.CHEVRON_DOWN}
          iconStart={IconVariants.ARROW_DOWN_UP}
          variant={ButtonVariant.OUTLINED}
        >
          {selectedLabel}
        </Button>
      </DropDownMenu>
    </div>
  );
};

export default React.memo(ChatsSortDropdown);
